'use client';

import { Container } from '@/components/ui/Container/Container';
import { LanguageSwitcher } from '@/components/features/LanguageSwitcher/LanguageSwitcher';
import { type Locale } from '@/lib/i18n/locales';
import { type Dictionary } from '@/content/i18n/schema';
import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import styles from './Header.module.scss';
import NavLink from '@/components/ui/NavLink/NavLink';
import LogoLink from '@/components/ui/LogoLink/LogoLink';

interface HeaderProps {
  locale: Locale;
  dict: Dictionary['header'];
}

export function Header({ locale, dict }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const getSectionHref = (hash: string) => ({ pathname: `/${locale}`, hash });
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      // Cancel any pending RAF to throttle updates
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      // Use requestAnimationFrame to batch scroll handling
      rafRef.current = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 20);
        rafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
      <Container size="xl" className={styles.content}>
        <LogoLink locale={locale} />
        <nav aria-label="Main navigation">
          <ul>
            <li>
              <NavLink
                href={getSectionHref('about')}
                currentPathname={pathname}
              >
                {dict.links.whoWeAre}
              </NavLink>
            </li>
            <li>
              <NavLink
                href={getSectionHref('cases')}
                currentPathname={pathname}
              >
                {dict.links.cases}
              </NavLink>
            </li>
            <li>
              <NavLink
                href={getSectionHref('services')}
                currentPathname={pathname}
              >
                {dict.links.services}
              </NavLink>
            </li>
            <li>
              <NavLink
                href={getSectionHref('results')}
                currentPathname={pathname}
              >
                {dict.links.results}
              </NavLink>
            </li>
            <li>
              <NavLink
                href={getSectionHref('plans-prices')}
                currentPathname={pathname}
              >
                {dict.links.plansAndPrices}
              </NavLink>
            </li>
            <li>
              <NavLink href={getSectionHref('team')} currentPathname={pathname}>
                {dict.links.team}
              </NavLink>
            </li>
          </ul>
        </nav>
        <LanguageSwitcher currentLocale={locale} />
      </Container>
    </header>
  );
}
